import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Pencil, X, Save, Truck, Trash2, Filter, ChevronDown, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  'Active': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'Out of Service': 'bg-red-500/15 text-red-400 border-red-500/30',
  'In Shop': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'Insurance Expired': 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  'Sold': 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  'Archived': 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

const CATEGORIES = ['Truck', 'Trailer', 'Heavy Equipment', 'Light Vehicle', 'Other'];
const STATUSES = ['Active', 'Out of Service', 'In Shop', 'Insurance Expired', 'Sold', 'Archived'];

function StatusBadge({ status }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[status] || 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30'}`}>
      {status}
    </span>
  );
}

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [statusCounts, setStatusCounts] = useState({});
  const [categoryCounts, setCategoryCounts] = useState({});
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const fetchVehicles = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterStatus) params.set('status', filterStatus);
      if (filterCategory) params.set('category', filterCategory);
      const r = await fetch(`${API}/api/vehicles?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) {
        setVehicles(d.vehicles);
        setStatusCounts(d.status_counts || {});
        setCategoryCounts(d.category_counts || {});
      }
    } catch { toast.error('Failed to load vehicles'); }
    finally { setLoading(false); }
  }, [search, filterStatus, filterCategory, token]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const emptyForm = { name: '', year: '', make: '', model: '', vin: '', body_type: '', license_plate: '', category: 'Truck', status: 'Active', notes: '' };

  const startCreate = () => { setCreating(true); setEditing(null); setForm(emptyForm); };
  const startEdit = (v) => { setEditing(v.id); setCreating(false); setForm({ ...v }); };
  const cancel = () => { setEditing(null); setCreating(false); };

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error('Name is required'); return; }
    try {
      const url = editing ? `${API}/api/vehicles/${editing}` : `${API}/api/vehicles`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (!r.ok) { toast.error('Save failed'); return; }
      toast.success(editing ? 'Vehicle updated' : 'Vehicle added');
      cancel();
      fetchVehicles();
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await fetch(`${API}/api/vehicles/${id}`, { method: 'DELETE', headers });
      toast.success('Vehicle deleted');
      fetchVehicles();
    } catch { toast.error('Delete failed'); }
  };

  const totalActive = statusCounts['Active'] || 0;
  const totalOOS = statusCounts['Out of Service'] || 0;
  const totalShop = statusCounts['In Shop'] || 0;
  const totalExpired = statusCounts['Insurance Expired'] || 0;

  return (
    <div data-testid="vehicles-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Vehicles & Equipment</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {vehicles.length} shown — {totalActive} Active, {totalOOS} OOS, {totalShop} In Shop, {totalExpired} Ins. Expired
          </p>
        </div>
        <Button data-testid="add-vehicle-btn" onClick={startCreate} size="sm"><Plus className="h-4 w-4 mr-1" />Add Vehicle</Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="vehicle-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search unit, make, model, VIN..." className="pl-9" />
        </div>
        <select data-testid="filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s} ({statusCounts[s] || 0})</option>)}
        </select>
        <select data-testid="filter-category" value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c} ({categoryCounts[c] || 0})</option>)}
        </select>
        {(filterStatus || filterCategory) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterStatus(''); setFilterCategory(''); }}>Clear</Button>
        )}
      </div>

      {/* Create Form (top - only for new) */}
      {creating && (
        <div data-testid="vehicle-form" className="bg-card border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">New Vehicle</h2>
            <Button variant="ghost" size="icon" onClick={cancel}><X className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div><label className="text-xs text-muted-foreground mb-1 block">Unit Name *</label><Input data-testid="input-name" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Year</label><Input value={form.year || ''} onChange={e => setForm(p => ({ ...p, year: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Make</label><Input value={form.make || ''} onChange={e => setForm(p => ({ ...p, make: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Model</label><Input value={form.model || ''} onChange={e => setForm(p => ({ ...p, model: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">VIN</label><Input value={form.vin || ''} onChange={e => setForm(p => ({ ...p, vin: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Body Type</label><Input value={form.body_type || ''} onChange={e => setForm(p => ({ ...p, body_type: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">License Plate</label><Input value={form.license_plate || ''} onChange={e => setForm(p => ({ ...p, license_plate: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Category</label>
              <select value={form.category || 'Other'} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select>
            </div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Status</label>
              <select value={form.status || 'Active'} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">{STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select>
            </div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Notes</label><Input value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="save-vehicle-btn" onClick={handleSave} size="sm"><Save className="h-4 w-4 mr-1" />Save</Button>
            <Button variant="outline" size="sm" onClick={cancel}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? <div className="text-center py-8 text-muted-foreground">Loading...</div> : (
        <div className="border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30 border-b">
                  <th className="text-left p-3 font-medium">Unit</th>
                  <th className="text-left p-3 font-medium hidden sm:table-cell">Year</th>
                  <th className="text-left p-3 font-medium">Make</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Model</th>
                  <th className="text-left p-3 font-medium hidden lg:table-cell">VIN</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Body Type</th>
                  <th className="text-left p-3 font-medium">Category</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-center p-3 font-medium" title="In Fleetio">F</th>
                  <th className="text-center p-3 font-medium" title="In Google Sheet">GS</th>
                  <th className="p-3 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v, i) => editing === v.id ? (
                  <tr key={v.id} data-testid={`vehicle-row-${i}`} className="border-b bg-muted/20">
                    <td colSpan={11} className="p-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-2">
                        <div><label className="text-xs text-muted-foreground block">Unit *</label><Input className="h-8 text-xs" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
                        <div><label className="text-xs text-muted-foreground block">Year</label><Input className="h-8 text-xs" value={form.year || ''} onChange={e => setForm(p => ({ ...p, year: e.target.value }))} /></div>
                        <div><label className="text-xs text-muted-foreground block">Make</label><Input className="h-8 text-xs" value={form.make || ''} onChange={e => setForm(p => ({ ...p, make: e.target.value }))} /></div>
                        <div><label className="text-xs text-muted-foreground block">Model</label><Input className="h-8 text-xs" value={form.model || ''} onChange={e => setForm(p => ({ ...p, model: e.target.value }))} /></div>
                        <div><label className="text-xs text-muted-foreground block">VIN</label><Input className="h-8 text-xs" value={form.vin || ''} onChange={e => setForm(p => ({ ...p, vin: e.target.value }))} /></div>
                        <div><label className="text-xs text-muted-foreground block">Body Type</label><Input className="h-8 text-xs" value={form.body_type || ''} onChange={e => setForm(p => ({ ...p, body_type: e.target.value }))} /></div>
                        <div><label className="text-xs text-muted-foreground block">Plate</label><Input className="h-8 text-xs" value={form.license_plate || ''} onChange={e => setForm(p => ({ ...p, license_plate: e.target.value }))} /></div>
                        <div><label className="text-xs text-muted-foreground block">Category</label>
                          <select value={form.category || 'Other'} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs">{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select>
                        </div>
                        <div><label className="text-xs text-muted-foreground block">Status</label>
                          <select value={form.status || 'Active'} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs">{STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select>
                        </div>
                        <div><label className="text-xs text-muted-foreground block">Notes</label><Input className="h-8 text-xs" value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
                      </div>
                      <div className="flex gap-2">
                        <Button data-testid="save-vehicle-btn" onClick={handleSave} size="sm" className="h-7 text-xs"><Save className="h-3.5 w-3.5 mr-1" />Save</Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={cancel}>Cancel</Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={v.id || i} data-testid={`vehicle-row-${i}`} className="border-b last:border-0 hover:bg-muted/10 transition-colors">
                    <td className="p-3 font-mono font-bold">{v.name}</td>
                    <td className="p-3 text-muted-foreground hidden sm:table-cell">{v.year}</td>
                    <td className="p-3">{v.make}</td>
                    <td className="p-3 text-muted-foreground hidden md:table-cell">{v.model}</td>
                    <td className="p-3 text-xs font-mono text-muted-foreground hidden lg:table-cell">{v.vin}</td>
                    <td className="p-3 text-muted-foreground hidden md:table-cell">{v.body_type}</td>
                    <td className="p-3"><span className="text-xs px-2 py-0.5 rounded bg-muted/30 text-muted-foreground">{v.category}</span></td>
                    <td className="p-3"><StatusBadge status={v.status} /></td>
                    <td className="p-3 text-center">{v.in_fleetio && <Check className="h-4 w-4 text-blue-400 inline-block" />}</td>
                    <td className="p-3 text-center">{v.in_google_sheet && <Check className="h-4 w-4 text-green-400 inline-block" />}</td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(v)} data-testid={`edit-vehicle-${i}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={() => handleDelete(v.id, v.name)} data-testid={`delete-vehicle-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {vehicles.length === 0 && (
                  <tr><td colSpan={11} className="text-center py-8 text-muted-foreground">No vehicles found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
